import React, { useState, useRef, useEffect } from 'react';
import { Eye, ArrowRight, BarChart3, Link2, CheckCircle2, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const [activePillarIndex, setActivePillarIndex] = useState(0);
  const pillarsRef = useRef(null);
  const navigate = useNavigate();

  const handleDemo = () => {
    navigate('/demo-preview');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handlePillarsScroll = () => {
    if (pillarsRef.current) {
      const scrollLeft = pillarsRef.current.scrollLeft;
      const cardWidth = pillarsRef.current.offsetWidth * 0.85;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActivePillarIndex(newIndex);
    }
  };

  useEffect(() => {
    const container = pillarsRef.current;
    container?.addEventListener('scroll', handlePillarsScroll);
    return () => container?.removeEventListener('scroll', handlePillarsScroll);
  }, []);

  const renderDotIndicator = (count, activeIndex) => (
    <div className="flex justify-center gap-2 mt-6">
      {[...Array(count)].map((_, index) => (
        <div
          key={index}
          className={`w-2 h-2 rounded-full transition-all duration-300 ${
            index === activeIndex ? 'bg-blue-400 w-4' : 'bg-slate-600'
          }`}
        />
      ))}
    </div>
  );

  const pillars = [
    {
      title: "One trackable link with entire pitch information",
      description: "Share a single link that contains your full pitch—deck, one-pager, and key materials. See when it’s opened and how it’s used.",
      color: "from-blue-500 to-blue-600",
      Icon: Link2
    },
    {
      title: "Track investors and outreach",
      description: "Manage every intro, meeting, and follow-up in one place. Know who’s engaged, who’s cold, and never drop a thread.",
      color: "from-green-500 to-teal-600",
      Icon: BarChart3
    },
    {
      title: "Track relevant accelerators and deadlines",
      description: "Keep accelerators, application windows, and key dates in one view so you never miss an opportunity.",
      color: "from-purple-500 to-violet-600",
      Icon: Calendar
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="text-center max-w-[88rem] mx-auto w-full">
          {/* Glow Effect */}
          <div className="absolute -z-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 sm:mb-6 px-2">
            Raise faster with one smart deck link.
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 mb-2 max-w-2xl mx-auto leading-relaxed px-2">
            Create a single link for your pitch, track every investor view, and manage your whole raise in one lightweight workspace.
          </p>
          <p className="text-sm sm:text-base text-slate-500 mb-8 px-2">
            DocSend + CRM, but built for scrappy founders. No enterprise bloat or $100/month pricing.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <button
              onClick={handleDemo}
              className="w-full sm:w-auto px-12 py-6 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-blue-400 text-white hover:text-blue-400 rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              <Eye className="w-6 h-6" />
              View Live Demo
            </button>
            
            <button
              onClick={handleSignUp}
              className="w-full sm:w-auto px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl text-xl font-medium transition-all flex items-center justify-center gap-3 group min-w-[280px]"
            >
              Get Started Free
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Messaging pillars (outcomes) */}
          <div className="mb-16">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-6 px-2">Why founders choose DeckLink</h2>
            <div
              className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-4 scrollbar-hide px-2 -mx-2 items-stretch"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              ref={pillarsRef}
              onScroll={handlePillarsScroll}
            >
              {pillars.map((pillar, index) => {
                const Icon = pillar.Icon;
                return (
                  <div key={index} className="flex-none w-[92vw] max-w-[420px] md:max-w-[480px] snap-start h-full">
                    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-10 hover:border-blue-500/30 transition-all duration-300 group h-full min-h-[380px] flex flex-col">
                      <div className="text-center flex flex-col flex-1">
                        <div className={`bg-gradient-to-r ${pillar.color} rounded-2xl p-5 w-fit mx-auto mb-6 group-hover:scale-110 transition-transform shrink-0`}>
                          <Icon className="text-white" size={40} />
                        </div>
                        <h3 className="text-xl md:text-2xl font-semibold text-white mb-4 shrink-0">{pillar.title}</h3>
                        <p className="text-slate-300 leading-relaxed text-base md:text-lg flex-1 min-h-0">{pillar.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {renderDotIndicator(pillars.length, activePillarIndex)}
          </div>

          {/* Founder pricing */}
          <section className="mb-16 flex flex-col items-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-2 px-4 text-center">Simple founder pricing</h2>
            <p className="text-slate-400 text-sm sm:text-base mb-6 px-4 text-center max-w-xl">
              One plan. Everything you need. Start with a <strong className="text-slate-300">7‑day free trial</strong>—no card required.
            </p>
            <div className="w-full max-w-md mx-auto bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 sm:p-8 hover:border-blue-500/30 transition-all duration-300">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-bold text-white">$5</span>
                <span className="text-slate-400">/month</span>
              </div>
              <p className="text-slate-500 text-sm mb-4">After your 7‑day free trial. Cancel anytime.</p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-slate-300 text-sm">
                  <Calendar className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span><strong className="text-slate-200">7‑day free trial</strong>—try everything, no card required</span>
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Unlimited uploads, analytics, custom branding, team collaboration</span>
                </li>
              </ul>
              <button
                onClick={handleSignUp}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-4 rounded-xl font-medium transition-all"
              >
                Start 7‑day free trial
                <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-slate-500 text-xs mt-4 text-center">
                Running a program? <Link to="/pricing" className="text-blue-400 hover:text-blue-300 underline">See team & program pricing</Link>
              </p>
            </div>
          </section>

          {/* Social proof */}
          <p className="text-slate-500 text-sm mt-8 font-light px-2">
            <strong className="text-slate-400">Built for real founders.</strong> Host your deck, track opens, and manage outreach in one place—without enterprise bloat or $100/month pricing.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;